import { useEffect, useRef } from 'react';
const IMPRESSION_DELAY_THRESHOLD = 1000; // 1 second delay threshold in milliseconds
const VISIBILITY_THRESHOLD = 0.5; // 50% of component must be visible
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';

const useSuperChatViewableImpression = ({ componentRef, prompts = [], sotFields = {}, dependencies = [] }) => {
    const impressionTracked = useRef(false);
    const visibilityTimerRef = useRef(null);
    const sendAnalytics = () => {
        // Format prompts as semicolon-separated string
        const promptsString =
            Array.isArray(prompts) && prompts.length > 0
                ? prompts
                    .map(p => p?.content || '')
                    .filter(Boolean)
                    .join(';')
                : '';
        const eventName = 'superchat viewable impression';
        const eventData = {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            sotV312: 'SuperChatView',
            sotV219: IMPRESSION_DELAY_THRESHOLD.toString(),
            sotV402: promptsString,
            ...sotFields
        };
        processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
            data: eventData
        });
    };

    useEffect(() => {
        // Set up intersection observer for viewable impression tracking
        if (!componentRef?.current || impressionTracked.current || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD && !impressionTracked.current) {
                        // Clear any existing timer
                        if (visibilityTimerRef.current) {
                            clearTimeout(visibilityTimerRef.current);
                        }

                        // Set timer for delayed tracking
                        visibilityTimerRef.current = setTimeout(() => {
                            // Check if component is still visible after delay
                            if (componentRef?.current) {
                                const rect = componentRef.current.getBoundingClientRect();
                                const stillVisible = rect.top < window.innerHeight && rect.bottom > 0;

                                if (stillVisible && !impressionTracked.current) {
                                    impressionTracked.current = true;
                                    // Build event data with base fields and page-specific SOT fields
                                    sendAnalytics();
                                }
                            }
                        }, IMPRESSION_DELAY_THRESHOLD);
                    }
                });
            },
            {
                threshold: VISIBILITY_THRESHOLD
            }
        );

        observer.observe(componentRef.current);

        const cleanup = () => {
            if (visibilityTimerRef.current) {
                clearTimeout(visibilityTimerRef.current);
            }

            observer.disconnect();
        };

        // eslint-disable-next-line consistent-return
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentRef?.current, prompts, JSON.stringify(sotFields), ...dependencies]);
};

export default useSuperChatViewableImpression;
